import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import api from '../services/api';

const AuthContext = createContext({});

// Tempo de inatividade para logout automático (2 horas em milissegundos)
const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000;

// Snapshot do usuário logado para boot OFFLINE do terminal (PDV): reload sem
// internet não pode deslogar o caixa — o token continua válido, só não dá para
// REVALIDAR agora. Apagado no logout.
const USER_CACHE_KEY = 'auth_user_cache';
const lerUsuarioCache = () => {
  try { return JSON.parse(localStorage.getItem(USER_CACHE_KEY)) || null; } catch (_) { return null; }
};
const gravarUsuarioCache = (u) => {
  try {
    if (u) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u));
    else localStorage.removeItem(USER_CACHE_KEY);
  } catch (_) { /* storage cheio/indisponível */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const inactivityTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Função para fazer logout por inatividade
  const logoutByInactivity = useCallback(async () => {
    // A política de 2h só vale com o servidor ALCANÇÁVEL: deslogar offline
    // trancaria o terminal do PDV para fora (login exige rede) e destruiria o
    // snapshot que permite o boot offline. axios cru: o interceptor de
    // services/api mascara o status real e re-tenta GETs sozinho.
    try {
      await axios.get('/api/auth/me', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        timeout: 4000
      });
    } catch (e) {
      const status = e?.response?.status;
      if (!status || status >= 500) {
        // Servidor fora do ar: mantém a sessão e re-verifica em 15 min
        console.warn('Inatividade de 2h com servidor inacessível — logout adiado');
        inactivityTimerRef.current = setTimeout(logoutByInactivity, 15 * 60 * 1000);
        return;
      }
      // 4xx: servidor respondeu — a política de inatividade se aplica
    }
    console.log('Sessão expirada por inatividade (2 horas)');
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');
    gravarUsuarioCache(null);
    setUser(null);
    navigate('/login');
  }, [navigate]);

  // Função para resetar o timer de inatividade
  const resetInactivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    localStorage.setItem('lastActivity', Date.now().toString());

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Só configura o timer se houver usuário logado
    if (user) {
      inactivityTimerRef.current = setTimeout(() => {
        logoutByInactivity();
      }, INACTIVITY_TIMEOUT);
    }
  }, [user, logoutByInactivity]);

  // Verificar se há token salvo ao carregar
  useEffect(() => {
    const checkUser = async () => {
      const token = localStorage.getItem('token');
      const lastActivity = localStorage.getItem('lastActivity');

      if (!token) {
        setLoading(false);
        return;
      }

      const inativoDemais = lastActivity
        ? (Date.now() - parseInt(lastActivity, 10)) > INACTIVITY_TIMEOUT
        : false;

      const limparSessao = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('lastActivity');
        gravarUsuarioCache(null);
        setUser(null);
      };

      try {
        // axios CRU de propósito: o interceptor de services/api (a) redireciona
        // em 401 com promise que nunca resolve, (b) re-empacota os demais erros
        // sem .response e (c) re-tenta GETs dobrando o timeout — tudo isso
        // quebraria a distinção rede-caída × token-inválido feita abaixo.
        const response = await axios.get('/api/auth/me', {
          headers: {
            Authorization: `Bearer ${token}`
          },
          timeout: 6000
        });

        if (response.status === 200 && response.data?.success) {
          if (inativoDemais) {
            // Política de inatividade (2h) vale quando o servidor é alcançável
            console.log('Sessão expirada por inatividade durante ausência');
            limparSessao();
          } else {
            setUser(response.data.data);
            gravarUsuarioCache(response.data.data);
            // Atualizar última atividade ao recarregar com sucesso
            localStorage.setItem('lastActivity', Date.now().toString());
          }
        } else {
          limparSessao();
        }
      } catch (error) {
        const status = error?.response?.status;
        if (status >= 400 && status < 500) {
          // O servidor RESPONDEU rejeitando (401/403 token inválido, 404
          // usuário excluído...) — aí sim deslogar.
          console.error('Sessão rejeitada pelo servidor:', status);
          limparSessao();
        } else {
          // Rede caída / servidor fora (timeout, 5xx): NÃO deslogar — o caixa
          // do PDV precisa abrir offline. Restaura o usuário do snapshot local
          // (inclusive com inatividade vencida: bloquear a loja inteira por
          // política de idle seria pior que o risco num terminal físico).
          // A revalidação acontece quando a conexão voltar: o heartbeat do PDV
          // recebe 401 e força novo login se o token tiver sido invalidado.
          const cached = lerUsuarioCache();
          if (cached) {
            console.warn('Servidor inacessível — sessão restaurada do cache local do terminal');
            setUser(cached);
          } else {
            // Sem snapshot não há o que restaurar; mantém o token para a
            // próxima carga tentar de novo quando a rede voltar.
            console.error('Erro ao buscar usuário (sem cache local):', error.message);
            setUser(null);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    checkUser();
  }, []);

  // Configurar monitoramento de atividade do usuário
  useEffect(() => {
    if (!user) {
      // Se não há usuário logado, limpar o timer
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      return;
    }

    // Eventos que indicam atividade do usuário
    const activityEvents = ['mousedown', 'keypress', 'mousemove', 'scroll', 'touchstart', 'click'];

    // Throttle para evitar muitas atualizações
    let lastReset = Date.now();
    const throttleMs = 30000; // Atualizar no máximo a cada 30 segundos

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastReset >= throttleMs) {
        lastReset = now;
        resetInactivityTimer();
      }
    };

    // Adicionar listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Iniciar o timer
    resetInactivityTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [user, resetInactivityTimer]);

  // Redirecionar para página de troca de senha se necessário
  useEffect(() => {
    if (!loading && user && user.deveTrocarSenha && location.pathname !== '/trocar-senha') {
      navigate('/trocar-senha');
    }
  }, [user, loading, location.pathname, navigate]);

  const login = async (email, senha) => {
    try {
      const response = await api.post('/api/auth/login', { email, senha });

      // A resposta do backend agora é direta: { message, token, usuario }
      // ou { success, data: { token, usuario } } dependendo do controller.
      // O usuarioController.js retorna: { message, token, usuario }

      const data = response.data;

      const token = data.token || (data.data && data.data.token);
      // Backend pode retornar 'user' ou 'usuario'
      const usuarioData = data.user || data.usuario || (data.data && data.data.usuario);
      // Backend pode retornar deveTrocarSenha na raiz ou dentro do usuario
      const deveTrocar = data.deveTrocarSenha || (usuarioData && (usuarioData.deveTrocarSenha || usuarioData.deve_trocar_senha));

      if (token && usuarioData) {
        localStorage.setItem('token', token);
        localStorage.setItem('lastActivity', Date.now().toString());

        // Garantir deveTrocarSenha no objeto usuario para consistência
        const usuarioCompleto = {
          ...usuarioData,
          deveTrocarSenha: !!deveTrocar
        };

        setUser(usuarioCompleto);
        gravarUsuarioCache(usuarioCompleto);

        if (deveTrocar) {
          return { success: true, deveTrocarSenha: true };
        }

        return { success: true, deveTrocarSenha: false };
      } else {
        throw new Error('Dados de login inválidos recebidos do servidor');
      }
    } catch (error) {
      console.error('Erro no login:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erro ao fazer login'
      };
    }
  };

  const register = async (nome, email, senha) => {
    try {
      const response = await api.post('/api/auth/register', { nome, email, senha });
      const data = response.data;

      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('lastActivity', Date.now().toString());
        setUser(data.usuario);
        gravarUsuarioCache(data.usuario);
        return { success: true };
      } else if (data.data && data.data.token) {
        localStorage.setItem('token', data.data.token);
        localStorage.setItem('lastActivity', Date.now().toString());
        setUser(data.data.usuario);
        gravarUsuarioCache(data.data.usuario);
        return { success: true };
      } else {
        throw new Error('Token não recebido');
      }
    } catch (error) {
      console.error('Erro no registro:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erro ao registrar'
      };
    }
  };

  const trocarSenha = async (novaSenha, confirmarSenha) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.post('/api/auth/trocar-senha',
        { novaSenha, confirmarSenha },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        // Atualizar usuário com deveTrocarSenha = false
        setUser(response.data.usuario);
        gravarUsuarioCache(response.data.usuario);
        return { success: true };
      } else {
        throw new Error(response.data.message || 'Erro ao trocar senha');
      }
    } catch (error) {
      console.error('Erro ao trocar senha:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erro ao trocar senha'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');
    gravarUsuarioCache(null);
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // LGPD: limpar dados sensíveis cacheados do PDV (catálogo + clientes).
    // Vendas pendentes ficam preservadas (perderia venda offline real).
    import('../pages/PDV/db').then(({ limparDadosSensiveis }) => {
      limparDadosSensiveis().catch((e) => console.warn('[PDV] limpar cache falhou:', e.message));
    }).catch(() => {/* PDV pode não estar disponível */});

    // Service Worker do PDV: invalida cache de catálogo/produto (não logout-aware)
    if ('caches' in window) {
      caches.keys().then((ks) =>
        Promise.all(ks.filter((k) => k.startsWith('pdv-sw-')).map((k) => caches.delete(k)))
      ).catch(() => {});
    }

    setUser(null);
    navigate('/login');
  };

  const isAuthenticated = () => {
    return !!user;
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const isFornecedor = () => {
    return user?.role === 'fornecedor';
  };

  // Acesso exclusivo a funcionalidades sensíveis (Histórico do Chat, IA Analytics)
  // Restrito aos IDs: 12 = Thiago, 6 = Paulo Cendon
  const isSuperAdmin = () => {
    return user?.id === 12 || user?.id === 6;
  };

  // Admin ROOT do sistema — acesso EXCLUSIVO (ex.: Roadmap de refatoração).
  // Só thiago@gmail.com (id 12). Nem outros admins entram. O backend valida
  // o mesmo em routes/roadmap.js — isto aqui é só para esconder o menu/rota.
  const isRootAdmin = () => {
    const email = (user?.email || '').toLowerCase().trim();
    return email === 'thiago@gmail.com' || user?.id === 12;
  };

  // ── Sistema novo de permissões granulares ────────────────────────────
  // O /me retorna user.permissoes = ['financeiro', 'octadesk', ...].
  // Use hasPermission no lugar de checagens hardcoded por id/role.
  // Regra: WHITELIST — admin não tem nada automaticamente.
  const hasPermission = (modulo) => {
    if (!user?.permissoes || !Array.isArray(user.permissoes)) return false;
    return user.permissoes.includes(modulo);
  };

  // Recarrega permissões do servidor (após mudanças via tela de gestão).
  const recarregarPermissoes = async () => {
    try {
      const r = await api.get('/api/usuarios/me');
      if (r.data?.success && r.data.data) {
        setUser(prev => {
          const atualizado = { ...prev, permissoes: r.data.data.permissoes || [] };
          gravarUsuarioCache(atualizado);
          return atualizado;
        });
      }
    } catch (e) {
      console.warn('recarregarPermissoes falhou:', e.message);
    }
  };

  const deveTrocarSenha = () => {
    return user?.deveTrocarSenha === true;
  };

  const getToken = () => {
    return localStorage.getItem('token');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        trocarSenha,
        logout,
        isAuthenticated,
        isAdmin,
        isFornecedor,
        isSuperAdmin,
        isRootAdmin,
        hasPermission,
        recarregarPermissoes,
        deveTrocarSenha,
        getToken
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};
